import React, { useEffect, useState } from 'react';
import { Film, CheckCircle2, AlertCircle, X, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import { useExportProgress } from '@/contexts/ExportProgressContext';
import { ExportPhase } from '@/lib/videoExport';
import { debugLog } from '@/lib/videoPlayerDebug';

/**
 * Inline "Download movie" progress toast.
 *
 * Mirrors UploadProgressToast (glassmorphism, minimized/expanded, swipe to
 * collapse) but for the single export task. Differences:
 *  - shows the current render phase, not a per-file count
 *  - exposes a Cancel button in expanded mode (export is long-running)
 *  - shows a friendly error message if the export fails
 *
 * Reads all state from ExportProgressContext, so it can be mounted once near the
 * app root and driven from anywhere.
 */

/**
 * The ordered pipeline shown as a stepper in the expanded toast.
 *
 * These three DISPLAY steps each cover a set of underlying export phases, so the
 * checklist reflects what actually happens. After the single-fetch refactor,
 * clip download + audio decode + frame render all happen together inside one
 * loop — so "Creating your video" stays active for the whole render (where the
 * "clip N of M" counter lives) instead of pretending download/audio finished
 * first. ('done' is the terminal success state, not its own step.)
 */
const EXPORT_STEPS: { label: string; phases: ExportPhase[] }[] = [
  { label: 'Preparing', phases: ['preparing'] },
  { label: 'Creating your video', phases: ['rendering'] },
  { label: 'Finishing up', phases: ['finalizing'] },
];

/** Index of the EXPORT_STEPS entry that owns the current phase. */
function activeStepIndex(phase: ExportPhase | undefined): number {
  if (!phase) return 0;
  const idx = EXPORT_STEPS.findIndex((s) => s.phases.includes(phase));
  // 'done' (or anything past the last step) → treat all steps as complete.
  return idx === -1 ? EXPORT_STEPS.length : idx;
}

export function ExportProgressToast() {
  const { status, progress, errorMessage, cancelExport, dismiss } = useExportProgress();

  const [isMinimized, setIsMinimized] = useState(false);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchOffset, setTouchOffset] = useState(0);

  const isRunning = status === 'running';
  const isSuccess = status === 'success';
  const isError = status === 'error';
  const isVisible = isRunning || isSuccess || isError;

  // Expand when a new export starts.
  useEffect(() => {
    if (isRunning) setIsMinimized(false);
  }, [isRunning]);

  // Auto-dismiss success after 3s (matches upload toast).
  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => dismiss(), 3000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, dismiss]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    const deltaX = e.touches[0].clientX - touchStart.x;
    const deltaY = Math.abs(e.touches[0].clientY - touchStart.y);
    if (deltaY < 50) setTouchOffset(deltaX);
  };
  const handleTouchEnd = () => {
    if (touchOffset > 100) setIsMinimized(true);
    else if (touchOffset < -100) setIsMinimized(false);
    setTouchOffset(0);
    setTouchStart(null);
  };

  const handleClick = () => {
    if (isSuccess) return;
    setIsMinimized((m) => !m);
  };

  if (!isVisible) return null;

  const percent = progress?.percent ?? 0;

  return (
    <div
      className={cn(
        isMinimized ? 'w-16 h-16' : 'w-full h-auto',
        'ml-auto',
        'bg-white/10 backdrop-blur-md border border-white/20',
        'rounded-lg shadow-lg',
        'pointer-events-auto',
        'overflow-hidden',
        'animate-in slide-in-from-bottom-5 duration-300',
        'transition-[width,height] duration-200',
        'origin-right',
        !isSuccess && 'cursor-pointer hover:bg-white/20',
      )}
      style={{ transform: touchOffset !== 0 ? `translateX(${touchOffset}px)` : undefined }}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      aria-label={isMinimized ? 'Click to expand download progress' : 'Click to minimize download progress'}
    >
      <div className={cn(isMinimized ? 'p-0 flex items-center justify-center h-full w-full' : 'p-3 space-y-2')}>
        {isMinimized && !isSuccess && !isError ? (
          <div className="flex flex-col items-center leading-tight">
            <div className="text-[9px] font-semibold text-white/80">Download</div>
            <div className="text-sm font-bold text-white">{Math.round(percent)}%</div>
          </div>
        ) : (
          <div className="flex items-start gap-2.5">
            <div className="flex-shrink-0 mt-0.5">
              {isSuccess ? (
                <CheckCircle2 className="w-4 h-4 text-green-300" />
              ) : isError ? (
                <AlertCircle className="w-4 h-4 text-yellow-300" />
              ) : (
                <Film className="w-4 h-4 text-white animate-pulse" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs font-semibold text-white">
                  {isSuccess ? 'Movie ready' : isError ? 'Download failed' : 'Creating your movie'}
                </div>
                {isRunning && (
                  <div className="text-base font-bold text-white flex-shrink-0">
                    {Math.round(percent)}%
                  </div>
                )}
              </div>

              {/* Step-by-step pipeline + warning while running */}
              {isRunning && (
                <>
                  <div className="mt-2 space-y-1">
                    {EXPORT_STEPS.map((step, stepIdx) => {
                      const currentIdx = activeStepIndex(progress?.phase);
                      const isDone = stepIdx < currentIdx;
                      const isActive = stepIdx === currentIdx;
                      // The active step shows the live detail (e.g. "Clip 66 of
                      // 559") on a second line so the counter is always visible.
                      const showDetail = isActive && progress?.phase === 'rendering' && progress.detail;
                      return (
                        <div
                          key={step.label}
                          className={cn(
                            'flex items-start gap-1.5 text-[11px]',
                            isActive ? 'text-white font-semibold' : isDone ? 'text-white/70' : 'text-white/40',
                          )}
                        >
                          <span className="flex-shrink-0 w-3 h-3 mt-0.5 flex items-center justify-center">
                            {isDone ? (
                              <Check className="w-3 h-3 text-green-300" />
                            ) : isActive ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <span className="w-1.5 h-1.5 rounded-full bg-white/30" />
                            )}
                          </span>
                          <span className="min-w-0">
                            {step.label}
                            {showDetail && (
                              <span className="block font-normal text-white/70">{progress.detail}</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 flex items-center gap-1 text-[10px] text-yellow-300">
                    <AlertCircle className="w-3 h-3 flex-shrink-0" />
                    <span>Please do not close this window</span>
                  </div>
                  <div className="mt-1.5">
                    <Progress value={percent} className="h-1.5 bg-white/20" />
                  </div>
                  {/* Cancel — only in expanded mode */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      debugLog.userAction("User clicked 'Cancel export'");
                      cancelExport();
                    }}
                    className="mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-md border border-white/30 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500/80 hover:border-red-400 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" /> Cancel download
                  </button>
                </>
              )}

              {isSuccess && (
                <div className="mt-0.5 text-[10px] text-white/70">
                  Your movie has been downloaded.
                </div>
              )}

              {isError && (
                <>
                  <div className="mt-0.5 text-[11px] text-white/80">
                    {errorMessage}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      debugLog.userAction("User dismissed export error");
                      dismiss();
                    }}
                    className="mt-2 flex items-center gap-1 text-[11px] text-white/70 hover:text-white"
                  >
                    <X className="w-3 h-3" /> Dismiss
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
